import React from 'react'
import styled, { useTheme } from 'styled-components'
import { Table, Avatar, Dropdown, ThreeDots } from '@lmc/ui-kit'
import { AlignType } from 'rc-table/lib/interface'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import dayjs from 'dayjs'
import ActionCell from './ActionCell'
import Success from '../../iconsV2/Success'
import Warning from '../../iconsV2/Warning'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import { Text } from '@lmc/ui-kit/src/general'
import {
  MedicationStockReason,
  StockRecord,
  DiscrepancyStatus
} from '../../types'

// Helper function to check if a record is an administration record
const isAdministrationRecord = (record: StockRecord): boolean => {
  return record.type === 'administration'
}

type StockRecordsTableProps = {
  records: StockRecord[]
  total: number
  onViewRecord: (id: string) => void
  onEditRecord: (id: string) => void
  onViewAdministrationRecord: (id: string) => void
}

export const StockRecordsTable = ({
  records,
  total,
  onViewRecord,
  onEditRecord,
  onViewAdministrationRecord
}: StockRecordsTableProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const [currentPage, setCurrentPage] = React.useState(1)

  React.useEffect(() => {
    setCurrentPage(1)
  }, [records.length])

  const tableColumns = [
    {
      title: t('TIME_RECORDED'),
      dataIndex: 'date',
      key: 'date',
      width: 140,
      render: (date: string) => (
        <Text>{dayjs(date).format('DD-MM-YYYY HH:mm')}</Text>
      )
    },
    {
      title: t('RECORDED_BY'),
      dataIndex: 'recordedBy',
      key: 'recordedBy',
      width: 200,
      render: (recordedBy: StockRecord['recordedBy'], record: StockRecord) => {
        // For administration records, use the administration.recordedBy if available
        if (
          isAdministrationRecord(record) &&
          record.administration?.recordedBy
        ) {
          const adminRecordedBy = record.administration.recordedBy
          const name =
            typeof adminRecordedBy.name === 'object'
              ? `${adminRecordedBy.name.first} ${adminRecordedBy.name.last}`
              : adminRecordedBy.name || ''
          return (
            <RecordedByCell>
              <Avatar
                url={adminRecordedBy.picture as unknown as string}
                size={30}
              />
              <CarerName>{name}</CarerName>
            </RecordedByCell>
          )
        }

        return (
          <RecordedByCell>
            <Avatar url={recordedBy?.picture as unknown as string} size={30} />
            <CarerName>{recordedBy?.name}</CarerName>
          </RecordedByCell>
        )
      }
    },
    {
      title: t('ACTION'),
      dataIndex: 'action',
      key: 'action',
      width: 180,
      render: (_: MedicationStockReason, record: StockRecord) => (
        <ActionCell record={record} t={t} />
      )
    },
    {
      title: (
        <ColumnHeaderWithIcon>
          {t('EXPECTED_STOCK')}
          <Tooltip
            title={t('EXPECTED_STOCK_TOOLTIP')}
            color={theme.color.neutral.color3}
            overlayInnerStyle={{
              color: '#fff',
              borderRadius: '9px',
              padding: '10px 16px'
            }}
          >
            <QuestionIcon>
              <QuestionMarkIcon />
            </QuestionIcon>
          </Tooltip>
        </ColumnHeaderWithIcon>
      ),
      dataIndex: 'expectedStock',
      key: 'expectedStock',
      width: 120,
      align: 'center' as AlignType,
      render: (expectedStock: number | undefined, record: StockRecord) => {
        // Administration records should show "-" for expected stock
        if (isAdministrationRecord(record)) {
          return <Text>-</Text>
        }

        // For added stock: expectedStock is resultingStockLevel
        // For stock checks: expectedStock is previousStockLevel
        const value =
          expectedStock ??
          (record.action === MedicationStockReason.RESTOCK ||
          record.action === MedicationStockReason.DISPOSAL
            ? record.resultingStockLevel
            : record.previousStockLevel)
        return (
          <Text>{value !== undefined && value !== null ? value : '-'}</Text>
        )
      }
    },
    {
      title: (
        <ColumnHeaderWithIcon>
          {t('STOCK_COUNT')}
          <Tooltip
            title={t('STOCK_COUNT_TOOLTIP')}
            color={theme.color.neutral.color3}
            overlayInnerStyle={{
              color: '#fff',
              borderRadius: '9px',
              padding: '10px 16px'
            }}
          >
            <QuestionIcon>
              <QuestionMarkIcon />
            </QuestionIcon>
          </Tooltip>
        </ColumnHeaderWithIcon>
      ),
      dataIndex: 'stockCount',
      key: 'stockCount',
      width: 120,
      align: 'center' as AlignType,
      render: (stockCount: number | undefined, record: StockRecord) => {
        // Administration records should show "-" for stock count
        if (isAdministrationRecord(record)) {
          return (
            <StockCountContainer>
              <StockCountValue>-</StockCountValue>
            </StockCountContainer>
          )
        }

        const hasDiscrepancy = record.hasDiscrepancy || !!record.discrepancy
        const isResolved =
          record.discrepancy?.status === DiscrepancyStatus.RESOLVED || false

        // Stock count only applies to stock checks/reconciliation
        // For deliveries/disposals, show "-"
        const shouldShowStockCount =
          record.action === MedicationStockReason.STOCK_COUNT ||
          record.action === MedicationStockReason.RECONCILIATION ||
          record.action === MedicationStockReason.INITIAL_STOCK

        const value = shouldShowStockCount
          ? (stockCount ?? record.resultingStockLevel)
          : undefined

        return (
          <StockCountContainer>
            <StockCountValue>
              {value !== undefined && value !== null ? value : '-'}
            </StockCountValue>
            {shouldShowStockCount && hasDiscrepancy && !isResolved && (
              <Tooltip
                title={
                  <TooltipContent>{t('DISCREPANCY_DETECTED')}</TooltipContent>
                }
              >
                <IconWrapper>
                  <Warning />
                </IconWrapper>
              </Tooltip>
            )}
            {shouldShowStockCount && hasDiscrepancy && isResolved && (
              <Tooltip
                title={
                  <TooltipContent>
                    <TooltipTitle>{t('RESOLVED')}</TooltipTitle>
                    <TooltipDivider />
                    <TooltipMessage>
                      {record.discrepancy?.reason || t('DISCREPANCY_RESOLVED')}
                    </TooltipMessage>
                  </TooltipContent>
                }
              >
                <IconWrapper>
                  <Success />
                </IconWrapper>
              </Tooltip>
            )}
          </StockCountContainer>
        )
      }
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'center' as AlignType,
      render: (_: any, record: StockRecord) => {
        // Administration records use onViewAdministrationRecord
        if (isAdministrationRecord(record)) {
          const actionItems = [
            {
              key: 'view',
              label: t('VIEW_RECORD'),
              onClick: () => onViewAdministrationRecord(record.id)
            }
          ]

          return (
            <ActionsContainer
              data-action-cell
              onClick={(e) => e.stopPropagation()}
            >
              <Dropdown
                menu={{ items: actionItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <ClickableIconWrapper>
                  <ThreeDots horizontal />
                </ClickableIconWrapper>
              </Dropdown>
            </ActionsContainer>
          )
        }

        const actionItems = [
          {
            key: 'view',
            label: t('VIEW_RECORD'),
            onClick: () => onViewRecord(record.id)
          }
        ]

        if (
          record.action !== MedicationStockReason.MED_ADMIN &&
          record.action !== MedicationStockReason.INITIAL_STOCK
        ) {
          actionItems.push({
            key: 'edit',
            label: t('EDIT_RECORD'),
            onClick: () => onEditRecord(record.id)
          })
        }

        return (
          <ActionsContainer
            data-action-cell
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown
              menu={{ items: actionItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <ClickableIconWrapper>
                <ThreeDots horizontal />
              </ClickableIconWrapper>
            </Dropdown>
          </ActionsContainer>
        )
      }
    }
  ]

  const pageSize = 10
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedRecords = records.slice(startIndex, endIndex)

  return (
    <Table
      columns={tableColumns}
      dataSource={paginatedRecords}
      rowKey="id"
      pagination={{
        current: currentPage,
        pageSize,
        total,
        showSizeChanger: false,
        onChange: (page) => setCurrentPage(page)
      }}
      data-testid="med-stock-records-table-v2"
      onRow={(record) => ({
        onClick: () => {
          if (isAdministrationRecord(record)) {
            onViewAdministrationRecord(record.id)
          } else {
            onViewRecord(record.id)
          }
        },
        style: { cursor: 'pointer' }
      })}
    />
  )
}

const RecordedByCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CarerName = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
`

const StockCountValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
`

const ColumnHeaderWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`

const StockCountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const IconWrapper = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  width: 14px;
`

const TooltipContent = styled.div`
  padding: 12px;
  min-width: 200px;
`

const TooltipTitle = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`

const TooltipDivider = styled.div`
  height: 1px;
  background-color: #d9d9d9;
  margin: 4px 0;
`

const TooltipMessage = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const ClickableIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color7};
    border-radius: 4px;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`
